// Form state for creating and editing a season, and the request bodies built
// from it. Pure, so the rules are tested without rendering. The backend still
// validates everything (dates, ranges, no emoji or "/word" in labels and the
// prize); the page shows its message.

import type { AdminSeason, SeasonInputBody, SpecialDay } from "../api/types";

export interface SpecialDayDraft {
  dayKey: string;
  label: string;
  multiplier: string;
}

export interface SeasonFormState {
  name: string;
  description: string;
  dayKeyFrom: string;
  dayKeyTo: string;
  status: "draft" | "announced" | "open";
  /** `datetime-local` value in the browser's time zone, "" for none. */
  opensAt: string;
  place: string[];
  raced: string;
  guessWin: string;
  specialDays: SpecialDayDraft[];
  prizeText: string;
  rulesText: string;
  countDaysBeforeJoin: boolean;
  reachRequiresInstalledApp: boolean;
}

/** The real 2026 season, as Nassos decided it. */
export const REAL_SEASON_FORM: SeasonFormState = {
  name: "The game",
  description: "45 morning races, 17 November to 31 December.",
  dayKeyFrom: "2026-11-17",
  dayKeyTo: "2026-12-31",
  status: "open",
  opensAt: "",
  place: ["5", "3", "2"],
  raced: "1",
  guessWin: "1",
  specialDays: [],
  prizeText: "An AOTD IRL Mini",
  rulesText: "",
  countDaysBeforeJoin: true,
  reachRequiresInstalledApp: false,
};

const pad = (n: number) => String(n).padStart(2, "0");

/** ISO instant to a `datetime-local` value in the browser's zone. */
export function isoToLocalInput(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** A `datetime-local` value (browser zone) to an ISO instant; "" is null. */
export function localInputToIso(value: string): string | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export function formFromSeason(s: AdminSeason): SeasonFormState {
  return {
    name: s.name,
    description: s.description ?? "",
    dayKeyFrom: s.dayKeyFrom,
    dayKeyTo: s.dayKeyTo,
    status: "open",
    opensAt: isoToLocalInput(s.opensAt),
    place: s.points.place.map(String),
    raced: String(s.points.raced),
    guessWin: String(s.points.guessWin),
    specialDays: s.specialDays.map((d) => ({ dayKey: d.dayKey, label: d.label, multiplier: String(d.multiplier) })),
    prizeText: s.prizeText ?? "",
    rulesText: s.rulesText ?? "",
    countDaysBeforeJoin: s.countDaysBeforeJoin,
    reachRequiresInstalledApp: s.reachRequiresInstalledApp,
  };
}

function wholeNumber(v: string, what: string): number {
  const t = v.trim();
  if (!/^\d+$/.test(t)) throw new Error(`${what} must be a whole number.`);
  return Number(t);
}

function parseSpecialDays(drafts: SpecialDayDraft[]): SpecialDay[] {
  return drafts.map((d, i) => {
    const n = i + 1;
    if (!d.dayKey) throw new Error(`Special day ${n} needs a date.`);
    if (!d.label.trim()) throw new Error(`Special day ${n} needs a label.`);
    const m = Number(d.multiplier);
    if (!d.multiplier.trim() || !Number.isFinite(m)) throw new Error(`Special day ${n} needs a multiplier.`);
    return { dayKey: d.dayKey, label: d.label.trim(), multiplier: m };
  });
}

/** Every editable field as the API wants it. Throws Error with a plain message on a bad number. */
export function bodyFromForm(f: SeasonFormState): Required<Omit<SeasonInputBody, "status">> {
  return {
    name: f.name.trim(),
    description: f.description,
    dayKeyFrom: f.dayKeyFrom,
    dayKeyTo: f.dayKeyTo,
    opensAt: localInputToIso(f.opensAt),
    points: {
      place: f.place.map((p, i) => wholeNumber(p, `Points for place ${i + 1}`)),
      raced: wholeNumber(f.raced, "Points for racing"),
      guessWin: wholeNumber(f.guessWin, "Points for the guessing"),
    },
    specialDays: parseSpecialDays(f.specialDays),
    prizeText: f.prizeText,
    rulesText: f.rulesText.trim() ? f.rulesText : null,
    countDaysBeforeJoin: f.countDaysBeforeJoin,
    reachRequiresInstalledApp: f.reachRequiresInstalledApp,
  };
}

export function createBody(f: SeasonFormState): SeasonInputBody {
  const body: SeasonInputBody = { ...bodyFromForm(f), status: f.status };
  if (!body.opensAt) delete body.opensAt;
  return body;
}

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/**
 * A PATCH body with only the fields that differ from the stored season, so an
 * untouched points table never triggers a re-settle. Dates are left out while
 * the backend locks them.
 */
export function patchBody(f: SeasonFormState, s: AdminSeason): SeasonInputBody {
  const next = bodyFromForm(f);
  const body: SeasonInputBody = {};
  if (next.name !== s.name) body.name = next.name;
  if (next.description !== (s.description ?? "")) body.description = next.description;
  if (!s.datesLocked) {
    if (next.dayKeyFrom !== s.dayKeyFrom) body.dayKeyFrom = next.dayKeyFrom;
    if (next.dayKeyTo !== s.dayKeyTo) body.dayKeyTo = next.dayKeyTo;
  }
  // compare at minute precision, which is what the input holds
  if (f.opensAt !== isoToLocalInput(s.opensAt)) body.opensAt = next.opensAt;
  if (!same(next.points, { place: s.points.place, raced: s.points.raced, guessWin: s.points.guessWin })) {
    body.points = next.points;
  }
  const stored = s.specialDays.map((d) => ({ dayKey: d.dayKey, label: d.label, multiplier: d.multiplier }));
  if (!same(next.specialDays, stored)) body.specialDays = next.specialDays;
  if (next.prizeText !== (s.prizeText ?? "")) body.prizeText = next.prizeText;
  if (next.rulesText !== (s.rulesText ?? null)) body.rulesText = next.rulesText;
  if (next.countDaysBeforeJoin !== s.countDaysBeforeJoin) body.countDaysBeforeJoin = next.countDaysBeforeJoin;
  if (next.reachRequiresInstalledApp !== s.reachRequiresInstalledApp) {
    body.reachRequiresInstalledApp = next.reachRequiresInstalledApp;
  }
  return body;
}
